import { clsx } from 'clsx';

interface Category {
  id: string;
  name: string;
  slug: string;
  templateCount: number;
  children: Category[];
}

interface CategoryTreeProps {
  categories: Category[];
  selectedId: string | null;
  onSelect: (categoryId: string | null) => void;
}

function CategoryNode({
  category,
  selectedId,
  onSelect,
  depth = 0,
}: {
  category: Category;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  depth?: number;
}) {
  const isSelected = category.id === selectedId;

  return (
    <div>
      <button
        type="button"
        className={clsx(
          'flex w-full items-center justify-between rounded-md px-3 py-1.5 text-sm',
          'transition-colors duration-150',
          isSelected
            ? 'bg-brand-50 text-brand-700 font-medium'
            : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary',
        )}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
        onClick={() => onSelect(isSelected ? null : category.id)}
      >
        <span className="truncate">{category.name}</span>
        {category.templateCount > 0 && (
          <span className="ml-2 shrink-0 text-xs text-text-tertiary">
            {category.templateCount}
          </span>
        )}
      </button>

      {category.children.length > 0 && (
        <div>
          {category.children.map((child) => (
            <CategoryNode
              key={child.id}
              category={child}
              selectedId={selectedId}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CategoryTree({ categories, selectedId, onSelect }: CategoryTreeProps) {
  return (
    <nav className="space-y-0.5">
      <button
        type="button"
        className={clsx(
          'flex w-full items-center rounded-md px-3 py-1.5 text-sm',
          'transition-colors duration-150',
          !selectedId
            ? 'bg-brand-50 text-brand-700 font-medium'
            : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary',
        )}
        onClick={() => onSelect(null)}
      >
        All Products
      </button>
      {categories.map((cat) => (
        <CategoryNode
          key={cat.id}
          category={cat}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
}
